import { useState, useMemo } from "react";
import {
  Box,
  Container,
  VStack,
  Text,
  Link,
  Heading,
  Select,
  Flex,
} from "@chakra-ui/react";
import { FiLink } from "react-icons/fi";
import ProjectInfoDisplay from "../../components/ProjectInfoDisplay";
import BuildStage from "../../components/BuildStage";
import { getStagesForBuild } from "../../util";
function CommunityMirrorView({ project, updates }: CommunityMirrorPipeline) {
  const [selectedBuildId, setSelectedBuildId] = useState<number>(
    updates[0]?.id,
  );

  const selectedBuild = useMemo(
    () => updates.find((update) => update.id === selectedBuildId),
    [updates, selectedBuildId],
  );

  const stages = useMemo(
    () => (selectedBuild ? getStagesForBuild(selectedBuild) : []),
    [selectedBuild],
  );

  // Get custom domain if available
  const customDomain =
    project.customProjectUrls && project.customProjectUrls.length > 0
      ? project.customProjectUrls[0].url
      : null;

  const viewSiteUrl = customDomain || project.hostedProjectUrl;

  return (
    <Container maxW="container.lg" py={{ base: 4, md: 8 }}>
      <Box
        bg="gray.50"
        p={{ base: 4, md: 6 }}
        borderRadius="lg"
        boxShadow="sm"
        border="1px solid"
        borderColor="gray.200"
      >
        {/* Project Info Section */}
        <VStack align="stretch" mb={{ base: 4, md: 6 }} spacing={3}>
          <Box>
            <Flex alignItems="center" gap={2} mb={2} flexWrap="wrap">
              <Heading size={{ base: "md", md: "lg" }}>{project.name}</Heading>
            </Flex>
            <Box color="gray.700" fontSize={{ base: "xs", md: "sm" }}>
              <ProjectInfoDisplay
                projectInfo={project.projectInfo}
                projectType={project.projectType}
              />

              {/* Display custom domain if available */}
              {viewSiteUrl && (
                <Flex alignItems="center" gap={1} justifyContent="left" my={1}>
                  <Box w="18px" h="18px">
                    <FiLink size={16} color="#637381" />
                  </Box>
                  <Link
                    href={`https://${viewSiteUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    display="inline-block"
                  >
                    <Text
                      fontSize={{ base: "xs", md: "sm" }}
                      isTruncated
                      maxW="100%"
                      overflowWrap="break-word"
                      wordBreak="break-all"
                    >
                      {viewSiteUrl}
                    </Text>
                  </Link>
                </Flex>
              )}
            </Box>
          </Box>
        </VStack>

        {/* Build Selection and Pipeline Status */}
        <VStack spacing={3} mb={{ base: 4, md: 6 }} align="stretch">
          <Heading size={{ base: "sm", md: "md" }}>
            Build Pipeline Status
          </Heading>
          <Select
            value={selectedBuildId}
            onChange={(e) => setSelectedBuildId(Number(e.target.value))}
            maxW="full"
            fontSize={{ base: "xs", md: "sm" }}
            bg="white"
          >
            {updates.map((build) => {
              const buildDate = new Date(build.timestamp).toLocaleString(
                "en-US",
                {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                },
              );

              const status =
                build.status === "pending"
                  ? "🔄"
                  : build.status === "success"
                    ? "✅"
                    : "❌";

              const buildInfo = [
                status,
                `Build #${build.id}`,
                buildDate,
                build.additionalProps?.branchName &&
                  `Branch: ${build.additionalProps.branchName}`,
                build.additionalProps?.lastCommitMessage &&
                  `${build.additionalProps.lastCommitMessage}`,
              ]
                .filter(Boolean)
                .join(" - ");

              return (
                <option key={build.id} value={build.id}>
                  {buildInfo}
                </option>
              );
            })}
          </Select>
        </VStack>
        <VStack spacing={{ base: 3, md: 4 }} align="stretch">
          {stages.map((stage, index) => (
            <BuildStage
              key={`${stage.name}-${index}`}
              stage={stage}
              selectedBuild={selectedBuild}
            />
          ))}
        </VStack>
      </Box>
    </Container>
  );
}

export default CommunityMirrorView;
